import { isEmpty, map, sumBy, toLower } from 'lodash';
import moment from 'moment';
import React, { useState, ReactNode } from 'react';
import { Checkbox } from '@mui/material';
import { Link } from 'react-router-dom';
import styles from '../../Deliveries.module.css';
import TableRow from '../../../ReusableComponents/Table/TableRow';
import TableCell from '../../../ReusableComponents/Table/TableCell';
import Dot from '../../../ReusableComponents/Dot/Dot';
import poItemsMissingDims from '../../../FnCatalogV2/utils/poItemsMissingDims';
import makeTopStatuses, {
  isOrderSelectable,
} from '../../../../Lib/Utils/Role/make-top-statuses';

const includesText = (value: string, text: string) =>
  toLower(value || '').includes(toLower(text || ''));

interface PurchaseOrder {
  internal_id: string;
  po_number: string;
  purchase_order_lines: Array<{ quantity: number; total: number }>;
  due_date: string;
  vendor_rejection_reason?: string;
  memo?: string;
}

interface VendorUser {
  id: string;
}

interface Props {
  purchaseOrder: PurchaseOrder;
  vendorUser: VendorUser | null;
  superuser: boolean;
  disabled?: boolean;
  selected?: boolean;
  toggleSelect: (purchaseOrder: PurchaseOrder, selected: boolean) => void;
}

const makeOrderRow = (
  order: PurchaseOrder,
  vendorUser: VendorUser | null,
  superuser: boolean,
  poId: string,
) => {
  const isPriority = includesText(order.memo ?? '', 'priority');
  const isCold = includesText(order.memo ?? '', 'cold');

  const getHighlightClass = () => {
    if (isPriority) return styles.priority;
    if (isCold) return styles.cold;
    return '';
  };
  const getHighlightType = () => {
    if (isPriority) return 'priority';
    if (isCold) return 'cold';
    return undefined;
  };
  return {
    poNumber: (
      <div className={styles.poNumberContainer}>
        {!isEmpty(vendorUser) ? (
          <Link
            target="_blank"
            to={`/users/${vendorUser?.id}/purchase-orders/${poId}`}
            className={`${styles.poLink} ${getHighlightClass()}`}
            data-highlight={getHighlightType()}
          >
            {order.po_number}
          </Link>
        ) : (
          <span className={getHighlightClass()}>{order.po_number}</span>
        )}
        {order.vendor_rejection_reason && (
          <span className={styles.poRejected}>This PO has been rejected</span>
        )}
      </div>
    ),
    units: sumBy(
      order.purchase_order_lines.filter((line) => line.total >= 0),
      'quantity',
    ),
    due_date: moment.utc(order.due_date).format('MM-DD-YYYY'),
    topStatus: (
      <div className={styles.poTopStatuses}>
        {map(makeTopStatuses(order, superuser), (ts, i) => (
          <div key={`${ts}_${i}`}>{ts}</div>
        ))}
      </div>
    ),
  };
};

function SelectablePurchaseOrder({
  purchaseOrder,
  toggleSelect,
  vendorUser = null,
  superuser = false,
  disabled = false,
  selected = false,
}: Props): ReactNode {
  const [isSelected, setIsSelected] = useState(selected);
  const dimsMissing = poItemsMissingDims(purchaseOrder);

  const handleCheckbox = () => {
    setIsSelected(!isSelected);
    toggleSelect(purchaseOrder, isSelected);
  };

  return (
    <TableRow className={styles.purchaseOrder}>
      {map(
        makeOrderRow(
          purchaseOrder,
          vendorUser,
          superuser,
          purchaseOrder.po_number,
        ),
        (value, name) => (
          <TableCell key={name} className={styles.purchaseOrderField}>
            {value}
          </TableCell>
        ),
      )}
      <TableCell padding="normal" align="left">
        <Dot color={dimsMissing === 0 ? 'green' : 'red'} />
        {dimsMissing !== 0 && (
          <Link
            to={`/users/${vendorUser?.id}/purchase-orders/${purchaseOrder.po_number}`}
          >
            Add
          </Link>
        )}
      </TableCell>
      <TableCell>
        <Checkbox
          color="secondary"
          onChange={handleCheckbox}
          className={styles.poSelect}
          checked={isSelected}
          disabled={disabled || !isOrderSelectable(purchaseOrder)}
        />
      </TableCell>
    </TableRow>
  );
}

export default SelectablePurchaseOrder;
